var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ExtensionMessageTarget, NetworkType, availableTransports } from '../..';
import { windowRef } from '../../MockWindow';
import { getTzip10Link } from '../../utils/get-tzip10-link';
import { isAndroid, isIOS } from '../../utils/platform';
import { desktopList, extensionList, iOSList, webList } from './wallet-lists';
const defaultExtensions = [
    'ookjlbkiijinhpmnjffcofjonbfbgaoc',
    'gpfndedineagiepkpinficbcbbgjoenn' // Beacon
];
export var Platform;
(function (Platform) {
    Platform[Platform["DESKTOP"] = 0] = "DESKTOP";
    Platform[Platform["IOS"] = 1] = "IOS";
    Platform[Platform["ANDROID"] = 2] = "ANDROID";
})(Platform || (Platform = {}));
export var WalletType;
(function (WalletType) {
    WalletType["IOS"] = "ios";
    WalletType["ANDROID"] = "android";
    WalletType["EXTENSION"] = "extension";
    WalletType["DESKTOP"] = "desktop";
    WalletType["WEB"] = "web";
})(WalletType || (WalletType = {}));
/**
 * @internalapi
 *
 */
export class Pairing {
    static getPlatfrom() {
        return __awaiter(this, void 0, void 0, function* () {
            return isAndroid(window) ? Platform.ANDROID : isIOS(window) ? Platform.IOS : Platform.DESKTOP;
        });
    }
    static getPairingInfo(pairingPayload, statusUpdateHandler, platform) {
        return __awaiter(this, void 0, void 0, function* () {
            const activePlatform = platform !== null && platform !== void 0 ? platform : (yield Pairing.getPlatfrom());
            const pairingCode = pairingPayload.p2pSyncCode;
            const postmessageSyncCode = pairingPayload.postmessageSyncCode;
            const preferredNetwork = pairingPayload.preferredNetwork;
            switch (activePlatform) {
                case Platform.DESKTOP:
                    return Pairing.getDesktopPairingAlert(pairingCode, statusUpdateHandler, postmessageSyncCode, preferredNetwork);
                case Platform.IOS:
                    return Pairing.getIOSPairingAlert(pairingCode, statusUpdateHandler, preferredNetwork);
                case Platform.ANDROID:
                    return Pairing.getAndroidPairingAlert(pairingCode, statusUpdateHandler, preferredNetwork);
                default:
                    throw new Error('platform unknown');
            }
        });
    }
    static getDesktopPairingAlert(pairingCode, statusUpdateHandler, postmessageSyncCode, network) {
        return __awaiter(this, void 0, void 0, function* () {
            const availableExtensions = yield availableTransports.availableExtensions;
            const qrLink = getTzip10Link('tezos://', pairingCode);
            availableExtensions.forEach((ext) => {
                const index = defaultExtensions.indexOf(ext.id);
                if (index >= 0) {
                    defaultExtensions.splice(index, 1);
                }
            });
            return {
                walletLists: [
                    {
                        title: 'Browser Extensions',
                        type: WalletType.EXTENSION,
                        wallets: [
                            ...availableExtensions.map((app) => {
                                var _a, _b, _c, _d;
                                const ext = extensionList.find((extEl) => extEl.id === app.id);
                                return {
                                    name: (_a = app.name) !== null && _a !== void 0 ? _a : ext === null || ext === void 0 ? void 0 : ext.name,
                                    logo: (_b = app.iconUrl) !== null && _b !== void 0 ? _b : ext === null || ext === void 0 ? void 0 : ext.logo,
                                    shortName: (_c = app.shortName) !== null && _c !== void 0 ? _c : ext === null || ext === void 0 ? void 0 : ext.shortName,
                                    color: (_d = app.color) !== null && _d !== void 0 ? _d : ext === null || ext === void 0 ? void 0 : ext.color,
                                    enabled: true,
                                    clickHandler() {
                                        if (postmessageSyncCode) {
                                            const message = {
                                                target: ExtensionMessageTarget.EXTENSION,
                                                payload: postmessageSyncCode,
                                                targetId: app.id
                                            };
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            windowRef.postMessage(message, windowRef.location.origin);
                                        }
                                        statusUpdateHandler(WalletType.EXTENSION, this);
                                    }
                                };
                            }),
                            ...extensionList
                                .filter((app) => defaultExtensions.some((extId) => extId === app.id))
                                .map((app) => ({
                                name: app.name,
                                shortName: app.shortName,
                                color: app.color,
                                logo: app.logo,
                                enabled: false,
                                clickHandler: () => {
                                    // Don't do anything
                                }
                            }))
                        ]
                    },
                    {
                        title: 'Desktop & Web Wallets',
                        type: WalletType.DESKTOP,
                        wallets: [
                            ...desktopList.map((app) => ({
                                name: app.name,
                                shortName: app.shortName,
                                color: app.color,
                                logo: app.logo,
                                enabled: true,
                                clickHandler() {
                                    const link = getTzip10Link(app.deepLink, pairingCode);
                                    window.open(link, '_blank');
                                    statusUpdateHandler(WalletType.DESKTOP, this);
                                }
                            })),
                            ...(yield Pairing.getWebList(pairingCode, statusUpdateHandler, network))
                        ]
                    }
                ],
                buttons: [],
                qrData: qrLink
            };
        });
    }
    static getIOSPairingAlert(pairingCode, statusUpdateHandler, network) {
        return __awaiter(this, void 0, void 0, function* () {
            const qrLink = getTzip10Link('tezos://', pairingCode);
            return {
                walletLists: [
                    {
                        title: 'Mobile Wallets',
                        type: WalletType.IOS,
                        wallets: iOSList.map((app) => ({
                            name: app.name,
                            shortName: app.shortName,
                            color: app.color,
                            logo: app.logo,
                            enabled: true,
                            clickHandler() {
                                var _a;
                                const link = getTzip10Link((_a = app.deepLink) !== null && _a !== void 0 ? _a : app.universalLink, pairingCode);
                                // iOS does not trigger deeplinks with `window.open(...)`. The only way is using a normal link. So we have to work around that.
                                const a = document.createElement('a');
                                a.setAttribute('href', link);
                                a.dispatchEvent(new MouseEvent('click', { view: window, bubbles: true, cancelable: true }));
                                statusUpdateHandler(WalletType.IOS, this);
                            }
                        }))
                    },
                    {
                        title: 'Web Wallets',
                        type: WalletType.WEB,
                        wallets: [...(yield Pairing.getWebList(pairingCode, statusUpdateHandler, network))]
                    }
                ],
                buttons: [],
                qrData: qrLink
            };
        });
    }
    static getAndroidPairingAlert(pairingCode, statusUpdateHandler, network) {
        return __awaiter(this, void 0, void 0, function* () {
            const qrLink = getTzip10Link('tezos://', pairingCode);
            return {
                walletLists: [
                    {
                        title: 'Web Wallets',
                        type: WalletType.WEB,
                        wallets: [...(yield Pairing.getWebList(pairingCode, statusUpdateHandler, network))]
                    }
                ],
                buttons: [
                    {
                        title: 'Mobile Wallets',
                        text: 'Connect Wallet',
                        clickHandler: () => {
                            window.open(qrLink, '_blank');
                            statusUpdateHandler(WalletType.ANDROID);
                        }
                    }
                ],
                qrData: qrLink
            };
        });
    }
    static getWebList(pairingCode, statusUpdateHandler, network) {
        return __awaiter(this, void 0, void 0, function* () {
            return webList.map((app) => ({
                name: app.name,
                shortName: app.shortName,
                color: app.color,
                logo: app.logo,
                enabled: true,
                clickHandler() {
                    var _a;
                    const link = getTzip10Link((_a = app.links[network]) !== null && _a !== void 0 ? _a : app.links[NetworkType.MAINNET], pairingCode);
                    window.open(link, '_blank');
                    statusUpdateHandler(WalletType.WEB, this);
                }
            }));
        });
    }
}
//# sourceMappingURL=Pairing.js.map